/**
 * Main App Component
 * Handles routing and layout for the HEAT Client Portal
 */
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import SelectClient from './pages/SelectClient';
import Dashboard from './pages/Dashboard';
import Issues from './pages/Issues';
import Stakeholders from './pages/Stakeholders';
import StakeholderDetail from './pages/StakeholderDetail';
import Insights from './pages/Insights';
import Monitoring from './pages/Monitoring';
import StakeholderMap from './pages/StakeholderMap';
import DecisionBoard from './pages/DecisionBoard';
import DailyBriefing from './pages/DailyBriefing';
import Settings from './pages/Settings';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import { NotificationsProvider } from './contexts/NotificationsContext';
import { TourProvider } from './components/tour/TourProvider';
import TourController from './components/tour/TourController';
import { auth } from './auth';
import { useDarkMode } from './hooks/useDarkMode';
import './App.css';

const App: React.FC = () => {
  useDarkMode();
  return (
    <NotificationsProvider>
      <Router>
        <TourProvider>
          <TourController />
          <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/select-client" element={<SelectClient />} />
        
        {/* Protected routes - Require authentication */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Layout>
                <Dashboard />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/daily-briefing"
          element={
            <ProtectedRoute>
              <Layout>
                <DailyBriefing />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/insights"
          element={
            <ProtectedRoute>
              <Layout>
                <Insights />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/decision-board"
          element={
            <ProtectedRoute>
              <Layout>
                <DecisionBoard />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/monitoring"
          element={
            <ProtectedRoute>
              <Layout>
                <Monitoring />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/issues"
          element={
            <ProtectedRoute>
              <Layout>
                <Issues />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/stakeholders"
          element={
            <ProtectedRoute>
              <Layout>
                <Stakeholders />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/stakeholders/:id"
          element={
            <ProtectedRoute>
              <Layout>
                <StakeholderDetail />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/stakeholder-map"
          element={
            <ProtectedRoute>
              <StakeholderMap />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Layout>
                <Settings />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        {/* Default route - redirect based on auth status */}
        <Route
          path="/"
          element={
            auth.isAuthenticated() ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        
        {/* Catch all - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </TourProvider>
      </Router>
    </NotificationsProvider>
  );
};

export default App;
